import type { KyInstance, Options as KyOptions } from 'ky';
import ky, { HTTPError } from 'ky';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { logError, suppressErrorLogging } from '@zg-rentals/log-error';
import { getTraceHeaders } from '@zg-rentals/trace';
import { makeAppInstance } from '@zg-rentals/util';

export type Client = KyInstance;
export type Options = KyOptions;
export { HTTPError };

const StartTime = Symbol('StartTime');

const Monitoring = Symbol('Monitoring');

const PX_BLOCKED_HEADER = 'x-px-blocked';

function isPerimeterXBlocked(response: Response | undefined): boolean {
  return Boolean(response?.headers.get(PX_BLOCKED_HEADER));
}

export const getClientOptions = (client: Client) =>
  ({
    monitoring: (client as any)[Monitoring] !== false, // eslint-disable-line @typescript-eslint/no-explicit-any
  }) as const;

/**
 * Create a new HTTP client instance with full control over configuration.
 *
 * @note typically you want to use `getClient().extend({...overrides})` to simply
 * override the default configuration value(s) you need to override
 *
 * @note if you are looking to configure code generated clients, import `getClient` from
 * `@zg-rentals/ts-client-http-client`
 */
export const createClient = ({
  logging,
  monitoring,
  ...options
}: KyOptions & {
  logging?: boolean;
  monitoring?: boolean;
} = {}): Client => {
  const logger = () => (logging ? getGlobalLogger('http-client') : undefined);
  const monitor = () => (monitoring ? getGlobalMonitor() : undefined);

  const client = ky
    .create({
      hooks: {
        beforeRequest: [
          (request, requestOptions) => {
            // Instrument outbound fetch with W3C trace context (undici bypasses the http.request interception).
            if (!request.headers.has('traceparent')) {
              const traceHeaders = getTraceHeaders();
              if (traceHeaders?.traceparent) {
                request.headers.set('traceparent', traceHeaders.traceparent);
                if (traceHeaders.tracestate) {
                  request.headers.set('tracestate', traceHeaders.tracestate);
                }
              }
            }

            monitor()?.count({ name: 'httpClient beforeRequest volume total', amount: 1 });

            monitor()?.count({
              name: `httpClient beforeRequest volume method ${request.method} total`,
              amount: 1,
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (requestOptions as any)[StartTime] = Date.now();

            logger()?.debug(
              {
                url: request.url,
                method: request.method,
              },
              'beforeRequest',
            );
          },
        ],

        afterResponse: [
          (request, requestOptions, response) => {
            monitor()?.count({ name: 'httpClient afterResponse volume total', amount: 1 });

            monitor()?.count({
              name: `httpClient afterResponse volume method ${request.method} total`,
              amount: 1,
            });

            monitor()?.count({
              name: `httpClient afterResponse volume statusCode ${response.status} total`,
              amount: 1,
            });

            if (isPerimeterXBlocked(response)) {
              monitor()?.count(
                { name: 'httpClient px_blocked total', amount: 1 },
                { name: `httpClient px_blocked method ${request.method} total`, amount: 1 },
                { name: `httpClient px_blocked statusCode ${response.status} total`, amount: 1 },
              );
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const latencyMs = (Date.now() - (requestOptions as any)[StartTime]) as number;

            monitor()?.gauges({
              name: 'httpClient afterResponse latencyMs',
              amount: latencyMs,
            });

            monitor()?.gauges({
              name: `httpClient afterResponse latencyMs method ${request.method}`,
              amount: latencyMs,
            });

            if (latencyMs >= 30 * 1000) {
              logger()?.warn(
                {
                  url: request.url,
                  method: request.method,
                  status: response.status,
                  latencyMs,
                },
                'afterResponse slow request',
              );
            } else {
              logger()?.debug(
                {
                  url: request.url,
                  method: request.method,
                  status: response.status,
                  latencyMs,
                },
                'afterResponse',
              );
            }
          },
        ],

        beforeError: [
          (error) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const latencyMs = (Date.now() - (error.options as any)[StartTime]) as number;

            monitor()?.count({ name: 'httpClient beforeError volume total', amount: 1 });

            monitor()?.count({
              name: `httpClient beforeError volume method ${error.request?.method ?? 'UNKNOWN'} total`,
              amount: 1,
            });

            monitor()?.count({
              name: `httpClient beforeError volume statusCode ${error.response?.status ?? 'UNKNOWN'} total`,
              amount: 1,
            });

            monitor()?.gauges({
              name: 'httpClient beforeError latencyMs',
              amount: latencyMs,
            });

            monitor()?.gauges({
              name: `httpClient beforeError latencyMs method ${error.request?.method ?? 'UNKNOWN'} total`,
              amount: latencyMs,
            });

            if (isPerimeterXBlocked(error.response)) {
              // Suppress regardless of `logging` so PX-blocked noise stays suppressed even for a
              // client that doesn't report its own generic errors - a later layer's `logError`
              // call (e.g. React Query) checks this same suppression state, so gating it behind
              // `logging` would let the PX noise leak through there instead.
              suppressErrorLogging(error);
            } else if (logging) {
              logError({
                error,
                context: {
                  url: error.request.url,
                  method: error.request.method,
                  status: error.response.status,
                  latencyMs,
                  traceparent: error.response.headers.get('traceparent'),
                  eid: error.response.headers.get('x-eid'),
                },
                errorType: 'HTTPError',
              });
            }

            return error;
          },
        ],
      },
    })
    .extend(options);

  // Note: this is an annoying hack, but allows us to scope monitoring/logging to the client, allowing
  // us to conditionally emit metrics in the codegen packages. This is necessary because we use the codegen
  // packages to emit metrics, so there is a recursive loop we need to avoid.
  (client as any)[Monitoring] = monitoring === true; // eslint-disable-line @typescript-eslint/no-explicit-any

  const extend = client.extend;
  client.extend = function (...args) {
    const extended = extend.call(this, ...args);
    (extended as any)[Monitoring] = monitoring === true; // eslint-disable-line @typescript-eslint/no-explicit-any
    return extended;
  };

  return client;
};

export const { get: getClient, set: setClient } = makeAppInstance(
  () => createClient({ logging: true, monitoring: true }),
  '@zg-rentals/http-client:client',
);
